import { Component, HostListener, OnInit } from '@angular/core';
import { FormBuilder, FormControl, Validators } from '@angular/forms';
import { ModalController } from '@ionic/angular';
import { map } from 'rxjs';
import { OPTIONS_MASK } from 'src/app/core/constants/options-mask.constant';
import { PedidoService } from 'src/app/core/services/http/pedido.service';
import { KeyboardKeysEnum } from '../../enums/keyboard-keys.enum';
import { ModalRoleEnum } from '../../enums/modal-role.enum';
import { OperacaoPgtoEnum } from '../../enums/operacao-pgto.enum';
import { SangriaSuprimentoRequest } from '../../models/sangria-suprimento-request';
import { HelpersUtil } from '../../utils/helpers.util';
import { DialogStateUtil } from '../../utils/dialog-state.util';
import { CaixaService } from 'src/app/core/services/http/caixa.service';
import { AuthenticationService } from 'src/app/core/services/http/authentication.service';
import { ImpressaoTermicaService } from 'src/app/core/services/behavior/impressao-termica.service';

@Component({
  selector: 'app-sangria-suprimento-dialog',
  templateUrl: './sangria-suprimento-dialog.component.html',
  //styleUrls: ['./sangria-suprimento-dialog.component.css']
})
export class SangriaSuprimentoDialogComponent implements OnInit {
  // componentProps
  public readonly operacaoCaixa: OperacaoPgtoEnum;
  //--
  public readonly optionsMask = OPTIONS_MASK;

  public form = this._formBuilder.group({
    codigoFormaPgtoCtrl: new FormControl<number>(1, [Validators.required]),
    valorCtrl: new FormControl<number>(0, [Validators.required]),
    motivoCtrl: new FormControl<string>('', [Validators.required]),
  });
  public formasPgto$ = this._pedidoService.getFormasPgto().pipe(map(l => l.success ? l.data : []));
  public request = new SangriaSuprimentoRequest();
  public comprovanteDisponivel = false;

  public get acaoRealizadaLabel(): string {
    return `${this._obterDescricaoOperacao()} realizada com sucesso.`;
  }

  private _mensagemComprovante = '';

  constructor(
    private _formBuilder: FormBuilder,
    private _modalCtrl: ModalController,
    private _pedidoService: PedidoService,
    private _caixaService: CaixaService,
    private _authService: AuthenticationService,
    private _impressaoTermicaService: ImpressaoTermicaService
  ) { }

  public async cancelarAsyncOnClick() {
    await this._modalCtrl.dismiss(null, ModalRoleEnum.CANCEL);
    
    DialogStateUtil.dialogIsClosed();
  }

  public async confirmarAsyncOnClick() {
    await this._confirmarAsync();
  }

  public mudarFocoInputsOnKeydownEnter(indexInputAtual: number): void {
    const inputs = this._obterInputs();
    const indexProximoInput = (indexInputAtual + 1) % inputs.length;
    const proximoInput = inputs[indexProximoInput];

    proximoInput.focus();
  }

  public ngOnInit() {
    HelpersUtil.executeAfterDelay(() => this._definirFocoPrimeiroInput());
  }

  @HostListener('window:keydown', ['$event'])
  public async _lidarComKeyboardEventAsync(event: KeyboardEvent): Promise<void> {
    if (event.key === KeyboardKeysEnum.F5) {
      event.preventDefault();
      event.stopPropagation();

      if (this.comprovanteDisponivel) {
        await this._imprimirComprovanteEFecharAsync();
        return;
      }

      if (!this.form.invalid) {
        await this._confirmarAsync();
      }
    }
  }

  private async _confirmarAsync() {
    if (this.comprovanteDisponivel) {
      await this._imprimirComprovanteEFecharAsync();
      return;
    }

    this.request.operacao = this.operacaoCaixa;
    this.request.numeroCaixa = this._authService.numeroCaixa;
    
    this._caixaService.inserirSangriaOuSuprimento(this.request).subscribe(async res => {
      if (res.success) {
        this._mensagemComprovante = this._obterMensagemParaImpressao(res.data, res.message);
        this.comprovanteDisponivel = true;
        this.form.disable({ emitEvent: false });
      }
    });
  }

  private async _imprimirComprovanteEFecharAsync(): Promise<void> {
    this._impressaoTermicaService.imprimirTexto(
      this._mensagemComprovante,
      '',
      {
        incluirCabecalho: true,
        titulo: this.operacaoCaixa === OperacaoPgtoEnum.Sangria ? 'SANGRIA' : 'SUPRIMENTO'
      }
    );

    await this._modalCtrl.dismiss(null, ModalRoleEnum.CONFIRM);
    DialogStateUtil.dialogIsClosed();
  }

  private _definirFocoPrimeiroInput(): void {
    const primeiroInput = this._obterPrimeiroInput();

    primeiroInput.focus();
  }

  private _obterInputs(): NodeListOf<HTMLInputElement> {
    return document.querySelectorAll('.control') as NodeListOf<HTMLInputElement>;
  }

  private _obterPrimeiroInput(): HTMLInputElement {
    return this._obterInputs().item(0);
  }

  private _obterMensagemParaImpressao(data: any, message: string): string {
    if (typeof data === 'string' && data.trim().length > 0) {
      return data;
    }

    if (typeof message === 'string' && message.trim().length > 0) {
      return message;
    }

    return `\nValor: ${this._formatarValor(this.request.valor)}\nMotivo: ${this.request.motivo || 'Nao informado.'}`;
  }

  private _obterDescricaoOperacao(): string {
    return this.operacaoCaixa === OperacaoPgtoEnum.Sangria ? 'Sangria' : 'Suprimento';
  }

  private _formatarValor(valor: number): string {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(valor || 0);
  }
}

export class SangriaSuprimentoDialogDataClose {
  constructor(
    public request: SangriaSuprimentoRequest
  ) { }
}
